import React, { useEffect, useState } from "react";
import ActionsFullScreenEnterNormalXsmall from "@strava/icons/ActionsFullScreenEnterNormalXsmall";
import ActionsFullScreenExitNormalXsmall from "@strava/icons/ActionsFullScreenExitNormalXsmall";
import { Button } from "@strava/ui/Button";

import I18n from "../../../utils/I18n";

import styles from "./FullscreenToggle.scss";

export const FullscreenToggle = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenEnabled =
    document.fullscreenEnabled || (document as any).webkitFullscreenEnabled;

  // Toggle body class to drive CSS-based layout changes while fullscreen.
  // Browsers with native Fullscreen API use "fullscreen"; those without
  // (older Safari, in-app WebViews) fall back to "fauxscreen" styling.
  useEffect(() => {
    const className = fullscreenEnabled ? "fullscreen" : "fauxscreen";

    if (isFullscreen) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
  }, [isFullscreen]);

  // Sync isFullscreen state when the browser fires fullscreenchange
  // (triggered after requestFullscreen/exitFullscreen resolves)
  useEffect(() => {
    const eventName = (document as any).webkitFullscreenEnabled
      ? "webkitfullscreenchange"
      : "fullscreenchange";

    document.addEventListener(eventName, handleFullscreenChange);

    return () => {
      document.removeEventListener(eventName, handleFullscreenChange);
    };
  }, []);

  // Use native Fullscreen API when supported; otherwise toggle local state
  // and let the useEffect apply fauxscreen CSS (covers older Safari, WebViews).
  const handleFullscreenClick = () => {
    const mapElement = document.getElementById("view");

    if (fullscreenEnabled && mapElement) {
      if (
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement
      ) {
        // .call() required due to OR detaching the method
        (
          document.exitFullscreen || (document as any).webkitExitFullscreen
        ).call(document);
      } else {
        // .call() required due to OR detaching the method
        (
          mapElement.requestFullscreen ||
          (mapElement as any).webkitRequestFullscreen
        ).call(mapElement);
      }
    } else {
      setIsFullscreen(prev => !prev);
    }
  };

  // Handler for fullscreen change event
  // Updates the isFullscreen state based on the current browser fullscreen element status
  const handleFullscreenChange = () => {
    setIsFullscreen(
      document.fullscreenElement || (document as any).webkitFullscreenElement
    );
  };

  return (
    <Button
      className={styles.fullscreenButton}
      data-testid="fullscreen-toggle-button"
      title={I18n.t(
        isFullscreen
          ? "mre.custom_control.exit_fullscreen_mode"
          : "mre.custom_control.enter_fullscreen_mode"
      )}
      onClick={handleFullscreenClick}
    >
      {isFullscreen ? (
        <ActionsFullScreenExitNormalXsmall data-testid="exit-fullscreen-icon" />
      ) : (
        <ActionsFullScreenEnterNormalXsmall data-testid="enter-fullscreen-icon" />
      )}
    </Button>
  );
};

export default FullscreenToggle;
